// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {appZoomCssPx, appZoomLayoutPx} from '@app/features/ui/utils/AppZoomUtils';
import type {Placement} from '@floating-ui/react-dom';
import {autoUpdate, computePosition, flip, offset, shift} from '@floating-ui/react-dom';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('useReactionTooltip');
interface ReactionTooltipState {
	x: number;
	y: number;
	isOpen: boolean;
	isReady: boolean;
}
export function useReactionTooltip(hoverDelay = 500, placement: Placement = 'top') {
	const targetRef = useRef<HTMLElement | null>(null);
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const cleanupRef = useRef<(() => void) | null>(null);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
	const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
	const isCalculatingRef = useRef(false);
	const lastPointerRef = useRef<{
		x: number;
		y: number;
	} | null>(null);
	const [state, setState] = useState<ReactionTooltipState>({
		x: 0,
		y: 0,
		isOpen: false,
		isReady: false,
	});
	const clearTimers = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);
	const isInHoverRegion = useCallback((node: EventTarget | null) => {
		if (!node || !(node instanceof Node)) return false;
		return !!targetRef.current?.contains(node) || !!tooltipRef.current?.contains(node);
	}, []);
	const updateLastPointer = useCallback((event: MouseEvent | React.MouseEvent | PointerEvent) => {
		lastPointerRef.current = {x: event.clientX, y: event.clientY};
	}, []);
	const shouldKeepOpen = useCallback(() => {
		const lastPointer = lastPointerRef.current;
		if (!lastPointer) return false;
		const element = document.elementFromPoint(lastPointer.x, lastPointer.y);
		return isInHoverRegion(element);
	}, [isInHoverRegion]);
	const updatePosition = useCallback(async () => {
		if (!state.isOpen || !targetRef.current || !tooltipRef.current || isCalculatingRef.current) {
			return;
		}
		if (!document.contains(targetRef.current)) {
			setState((prev) => ({...prev, isOpen: false, isReady: false}));
			return;
		}
		isCalculatingRef.current = true;
		try {
			const target = targetRef.current;
			const tooltip = tooltipRef.current;
			Object.assign(tooltip.style, {
				position: 'fixed',
				left: '-9999px',
				top: '-9999px',
			});
			const middleware = [offset(8), flip(), shift({padding: 8})];
			const {x, y} = await computePosition(target, tooltip, {
				placement,
				middleware,
			});
			Object.assign(tooltip.style, {
				left: appZoomCssPx(x),
				top: appZoomCssPx(y),
			});
			setState((prev) => ({...prev, x: appZoomLayoutPx(x), y: appZoomLayoutPx(y), isReady: true}));
		} catch (error) {
			logger.error('Error positioning reaction tooltip', error);
		} finally {
			isCalculatingRef.current = false;
		}
	}, [state.isOpen, placement]);
	const show = useCallback(() => {
		clearTimers();
		setState((prev) => ({...prev, isOpen: true, isReady: false}));
	}, [clearTimers]);
	const hide = useCallback(() => {
		clearTimers();
		setState({x: 0, y: 0, isOpen: false, isReady: false});
	}, [clearTimers]);
	const scheduleHide = useCallback(() => {
		closeTimerRef.current = setTimeout(() => {
			if (shouldKeepOpen()) {
				return;
			}
			hide();
		}, 100);
	}, [hide, shouldKeepOpen]);
	const handleMouseEnter = useCallback(() => {
		clearTimers();
		if (state.isOpen) return;
		hoverTimerRef.current = setTimeout(() => {
			show();
		}, hoverDelay);
	}, [show, hoverDelay, clearTimers, state.isOpen]);
	const handleMouseLeave = useCallback(
		(event: React.MouseEvent) => {
			clearTimers();
			updateLastPointer(event);
			if (isInHoverRegion(event.relatedTarget)) {
				return;
			}
			scheduleHide();
		},
		[clearTimers, isInHoverRegion, scheduleHide, updateLastPointer],
	);
	const handleFocus = useCallback(() => {
		clearTimers();
		if (!state.isOpen) {
			show();
		}
	}, [clearTimers, show, state.isOpen]);
	const handleBlur = useCallback(() => {
		clearTimers();
		scheduleHide();
	}, [clearTimers, scheduleHide]);
	const handleTooltipMouseEnter = useCallback(
		(event: React.MouseEvent) => {
			clearTimers();
			updateLastPointer(event);
		},
		[clearTimers, updateLastPointer],
	);
	const handleTooltipMouseLeave = useCallback(
		(event: React.MouseEvent) => {
			clearTimers();
			updateLastPointer(event);
			if (isInHoverRegion(event.relatedTarget)) {
				return;
			}
			scheduleHide();
		},
		[clearTimers, isInHoverRegion, scheduleHide, updateLastPointer],
	);
	useEffect(() => {
		if (!state.isOpen || !targetRef.current || !tooltipRef.current) {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
			return;
		}
		if (!document.contains(targetRef.current)) {
			setState({x: 0, y: 0, isOpen: false, isReady: false});
			return;
		}
		updatePosition();
		cleanupRef.current = autoUpdate(targetRef.current, tooltipRef.current, updatePosition, {
			ancestorScroll: true,
			ancestorResize: true,
			elementResize: true,
			layoutShift: true,
		});
		return () => {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [state.isOpen, updatePosition]);
	useEffect(() => {
		if (!state.isOpen) {
			return;
		}
		const handlePointerMove = (event: PointerEvent | MouseEvent) => {
			lastPointerRef.current = {x: event.clientX, y: event.clientY};
		};
		window.addEventListener('pointermove', handlePointerMove, {passive: true});
		window.addEventListener('mousemove', handlePointerMove, {passive: true});
		return () => {
			window.removeEventListener('pointermove', handlePointerMove);
			window.removeEventListener('mousemove', handlePointerMove);
		};
	}, [state.isOpen]);
	useEffect(() => {
		return () => {
			clearTimers();
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [clearTimers]);
	return {
		targetRef,
		tooltipRef,
		state,
		updatePosition,
		show,
		hide,
		handlers: {
			onMouseEnter: handleMouseEnter,
			onMouseLeave: handleMouseLeave,
			onFocus: handleFocus,
			onBlur: handleBlur,
		},
		tooltipHandlers: {
			onMouseEnter: handleTooltipMouseEnter,
			onMouseLeave: handleTooltipMouseLeave,
		},
	};
}
